import type { JSX } from 'react';
import { Outlet } from 'react-router-dom';

import { ReportIssueWidget } from '@/components/issue-reporting/ReportIssueWidget';
import { Sidebar } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';

/**
 * Authenticated app shell.
 *
 * Layout (per UI Guide §10.1):
 *   Desktop (≥md):
 *   ┌────────────┬──────────────────────────────────┐
 *   │            │ Topbar (sticky, h-14)            │
 *   │  Sidebar   ├──────────────────────────────────┤
 *   │            │ Content (max-w-7xl, p-6)         │
 *   └────────────┴──────────────────────────────────┘
 *
 *   Mobile (<md):
 *   ┌──────────────────────────────────────────────┐
 *   │ Topbar (hamburger + bell + user menu)        │
 *   ├──────────────────────────────────────────────┤
 *   │ Content (full-width, p-4)                    │
 *   └──────────────────────────────────────────────┘
 *   Sidebar slides in as a left Sheet drawer.
 *
 * Used as a route element by plan 03-04 (`<Route element={<AppShell />} />`). All authed pages
 * render inside the <Outlet/>.
 */
export function AppShell(): JSX.Element {
  return (
    <div className="flex min-h-svh w-full bg-background text-foreground">
      {/* Desktop sidebar — hidden on mobile, shown on md+ */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      {/* Mobile drawer sidebar (rendered inside Sheet, mounted at md:hidden) */}
      <Sidebar mobileDrawer />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar />
        <main className="flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
      {/* Global "Report an issue" widget — present on every authenticated screen. */}
      <ReportIssueWidget />
    </div>
  );
}
